import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Button } from 'primereact/button';
import { FileUpload } from 'primereact/fileupload';
import { Toast } from 'primereact/toast';
import { BlockUI } from 'primereact/blockui';
import { ProgressSpinner } from 'primereact/progressspinner';
import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog';
import { useLanguage } from '../../../../contexts/LanguageContext';
import { USER_COLORS, CHAT_MESSAGE_MAX_LENGTH } from '../constants/formConstants';
import { useUploadCommentDocumentMutation,useGetCommentDocumentsByIdQuery,useDeleteCommentDocumentMutation, useDownloadCommentDocumentsMutation } from '../../../../api/endpoints/technicalSupportRequest';
import { useLazyDownloadDocumentByKeyQuery } from '../../../../api/endpoints/documentContent';
import DocumentDownloadService from '../../../../utils/documentDownloadService';
import toastService from '../../../../utils/toastService';
import { techSupportChatService } from '../../../../services/techSupportChatService';
import { setCurrentTicket, clearTicketComments, selectRealtimeComments, selectIsConnected } from '../../../../store/slices/techSupportChatSlice';
import CommentDocuments from './CommentDocuments';
import { getCommentId, getFileKey } from '../utils/documentUtils';
import RichTextEditor from '../../../../components/common/RichTextEditor';

const getUserColor = (userName) => {
    let hash = 0;
    for (let i = 0; i < userName.length; i++) {
        hash = userName.charCodeAt(i) + ((hash << 5) - hash);
    }
    return USER_COLORS[Math.abs(hash) % USER_COLORS.length];
};

const getDownloadFileName = (contentDisposition, fallbackFileName) => {
    const match = contentDisposition?.match(/filename="?([^";]+)"?/i);
    return match?.[1] || fallbackFileName;
};

const ChatHistory = ({ chatHistory, ticketId, newMessage, onMessageChange, onSendMessage, isSending, currentUserEmail, currentUserId, onRefreshRequest, onUploadStateChange, disabled = false }) => {
    const { th } = useLanguage();
    const dispatch = useDispatch();
    const [canSendMessage, setCanSendMessage] = useState(false);

    const handleEditorValidityChange = useCallback(({ canSubmit }) => setCanSendMessage(canSubmit), []);
    const realtimeComments = useSelector(selectRealtimeComments(ticketId));
    const isConnected = useSelector(selectIsConnected);
    const toastRef = useRef(null);
    const [downloadService] = useState(() => new DocumentDownloadService(toastRef));
    const messageFileUploadRef = useRef(null);
    const [isUploading, setIsUploading] = useState(false);
    const [pendingFiles, setPendingFiles] = useState([]);
    const [pendingCommentId, setPendingCommentId] = useState(null);
    const [uploadingCommentId, setUploadingCommentId] = useState(null);
    const [commentDocumentStates, setCommentDocumentStates] = useState({});
    const chatContainerRef = useRef(null);
    const shouldAutoScrollRef = useRef(true);
    const previousCommentCountRef = useRef(0);

    const [uploadCommentDocument] = useUploadCommentDocumentMutation();
    const [deleteCommentDocument] = useDeleteCommentDocumentMutation();
    const [downloadCommentDocuments, { isLoading: isDownloadingCommentDocuments }] = useDownloadCommentDocumentsMutation();
    const [triggerDownloadByKey] = useLazyDownloadDocumentByKeyQuery();
    const isFileUploadInProgress = isUploading || uploadingCommentId !== null;

    useEffect(() => {
        onUploadStateChange?.(isFileUploadInProgress);

        return () => onUploadStateChange?.(false);
    }, [isFileUploadInProgress, onUploadStateChange]);

    const onRefreshRequestRef = useRef(onRefreshRequest);
    useEffect(() => {
        onRefreshRequestRef.current = onRefreshRequest;
    }, [onRefreshRequest]);

    useEffect(() => {
        shouldAutoScrollRef.current = true;
        previousCommentCountRef.current = 0;
        setCommentDocumentStates({});
        setPendingFiles([]);
        setPendingCommentId(null);
        setUploadingCommentId(null);
    }, [ticketId]);

    // SignalR connection lifecycle + listen for incoming comments
    useEffect(() => {
        if (!ticketId) return;

        const initSignalR = async () => {
            try {
                await techSupportChatService.connect();
                await techSupportChatService.joinTicketGroup(ticketId);
                dispatch(setCurrentTicket(ticketId));
            } catch (err) {
                console.error('SignalR init failed:', err);
            }
        };

        initSignalR();

        const unsubscribe = techSupportChatService.onCommentReceived(() => {
            onRefreshRequestRef.current?.();
        });

        return () => {
            unsubscribe();
            techSupportChatService.leaveTicketGroup(ticketId);
            dispatch(clearTicketComments(ticketId));
        };
    }, [ticketId, dispatch]);

    // Polling: fetch new messages every 5 seconds while chat is open
    useEffect(() => {
        if (!ticketId) return;

        const POLL_INTERVAL_MS = 5000;
        const intervalId = setInterval(() => {
            onRefreshRequestRef.current?.();
        }, POLL_INTERVAL_MS);

        return () => clearInterval(intervalId);
    }, [ticketId]);

    // Merge existing chat history with realtime comments, deduplicate by ID
    const allComments = useMemo(() => {
        const existing = chatHistory || [];
        const realtime = realtimeComments || [];

        const commentMap = new Map();
        [...existing, ...realtime].forEach((comment) => {
            const id = getCommentId(comment);
            if (id) {
                commentMap.set(id, comment);
            }
        });

        return Array.from(commentMap.values()).sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));
    }, [chatHistory, realtimeComments]);

    const scrollToBottom = useCallback(() => {
        if (!chatContainerRef.current) return;
        chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
    }, []);

    const handleChatScroll = useCallback(() => {
        if (!chatContainerRef.current) return;

        const { scrollTop, scrollHeight, clientHeight } = chatContainerRef.current;
        const distanceFromBottom = scrollHeight - (scrollTop + clientHeight);
        shouldAutoScrollRef.current = distanceFromBottom < 60;
    }, []);

    // Scroll to bottom when comments change only if user is near bottom
    useEffect(() => {
        if (allComments.length === 0) {
            previousCommentCountRef.current = 0;
            return;
        }

        const isFirstLoad = previousCommentCountRef.current === 0;
        const hasNewMessage = allComments.length > previousCommentCountRef.current;

        if (isFirstLoad || (hasNewMessage && shouldAutoScrollRef.current)) {
            scrollToBottom();
            setTimeout(scrollToBottom, 150);
        }

        previousCommentCountRef.current = allComments.length;
    }, [allComments, scrollToBottom]);

    const handleSendMessage = async () => {
        setIsUploading(true);
        try {
            const commentId = pendingCommentId || (await onSendMessage());
            if (!commentId) return;

            setPendingCommentId(commentId);
            for (const file of pendingFiles) {
                await uploadCommentDocument({
                    CommentId: commentId,
                    file,
                    description: file.name
                }).unwrap();
                setPendingFiles((currentFiles) => currentFiles.filter((pendingFile) => pendingFile !== file));
            }

            if (pendingFiles.length > 0) toastService.success(th('technicalSupport.messages.uploadDocumentSuccess'));
            setPendingFiles([]);
            setPendingCommentId(null);
            messageFileUploadRef.current?.clear();
            onMessageChange('');
            onRefreshRequestRef.current?.();
        } catch (error) {
            toastService.error(error?.data?.errorMessage || th('technicalSupport.messages.uploadDocumentError'));
        } finally {
            setIsUploading(false);
        }
    };

    const handleMessageFileSelect = (event) => {
        setPendingFiles((currentFiles) => [...currentFiles, ...event.files]);
        messageFileUploadRef.current?.clear();
    };

    const removePendingFile = (fileToRemove) => {
        setPendingFiles((currentFiles) => currentFiles.filter((file) => file !== fileToRemove));
    };

    const handleDeleteDocument = (commentId, documentId, fileName, isCommentOwner) => {
        if (!commentId || !documentId) {
            toastService.error(th('technicalSupport.messages.missingFileInfo'));
            return;
        }

        if (!isCommentOwner) {
            toastService.error(th('messages.unauthorizedError'));
            return;
        }

        const confirmMessage = th('technicalSupport.deleteDocumentConfirm', { fileName: fileName || 'File' });
        confirmDialog({
            message: confirmMessage,
            header: th('common.deleteConfirmTitle'),
            icon: 'pi pi-exclamation-triangle',
            acceptLabel: th('common.yes'),
            rejectLabel: th('common.no'),
            acceptClassName: 'p-button-danger',
            rejectClassName: 'p-button-outlined',
            accept: async () => {
                try {
                    await deleteCommentDocument({
                        Id: Number(commentId),
                        DocumentId: Number(documentId)
                    }).unwrap();

                    toastService.success(th('technicalSupport.messages.deleteDocumentSuccess'));
                } catch (error) {
                    toastService.error(error?.data?.errorMessage || th('technicalSupport.messages.deleteDocumentError'));
                }
            }
        });
    };

    const handleUploadDocument = async (commentId, files) => {
        if (!commentId || !files?.length) return;

        setUploadingCommentId(commentId);
        try {
            for (const file of files) {
                await uploadCommentDocument({
                    CommentId: commentId,
                    file,
                    description: file.name
                }).unwrap();
            }
            toastService.success(th('technicalSupport.messages.uploadDocumentSuccess'));
        } catch (error) {
            toastService.error(error?.data?.errorMessage || th('technicalSupport.messages.uploadDocumentError'));
        } finally {
            setUploadingCommentId(null);
        }
    };

    const handleDownloadDocument = async (doc) => {
        const key = getFileKey(doc);
        if (!key) return;

        if (toastRef?.current) {
            toastRef.current.show({
                severity: 'info',
                summary: th('common.downloading'),
                detail: th('common.downloading'),
                life: 3000
            });
        }

        try {
            await downloadService.downloadByKey(triggerDownloadByKey, key, doc, {
                successMessage: th('technicalSupport.messages.downloadDocumentSuccess'),
                errorMessage: th('technicalSupport.messages.downloadDocumentError')
            });
        } catch (error) {
            toastService.error(th('technicalSupport.messages.downloadDocumentError'));
        }
    };

    const handleCommentDocumentsChange = useCallback((commentId, state) => {
        setCommentDocumentStates((currentStates) => {
            if (!state) {
                const nextStates = { ...currentStates };
                delete nextStates[commentId];
                return nextStates;
            }

            const currentState = currentStates[commentId];
            if (currentState?.isLoading === state.isLoading && currentState?.documentIds?.join(',') === state.documentIds.join(',')) return currentStates;
            return { ...currentStates, [commentId]: state };
        });
    }, []);

    const commentIds = useMemo(() => allComments.map(getCommentId).filter(Boolean), [allComments]);
    const areCommentDocumentsLoading = commentIds.some((commentId) => !commentDocumentStates[commentId] || commentDocumentStates[commentId].isLoading);
    const commentDocumentIds = [...new Set(commentIds.flatMap((commentId) => commentDocumentStates[commentId]?.documentIds || []))];

    const handleDownloadAllCommentDocuments = async () => {
        if (!ticketId || commentDocumentIds.length === 0) return;

        try {
            const result = await downloadCommentDocuments({
                technicalSupportRequestId: Number(ticketId),
                documentIds: commentDocumentIds
            }).unwrap();
            downloadService.downloadBlob(result.blob, getDownloadFileName(result.contentDisposition, `technical-support-${ticketId}-comment-documents.zip`), false);
            toastService.success(th('technicalSupport.messages.downloadDocumentSuccess'));
        } catch (error) {
            toastService.error(th('technicalSupport.messages.downloadDocumentError'));
        }
    };

    return (
        <>
            {/* Chat History Section Header */}
            <div className="col-12">
                <h3 className="text-white p-3 mb-3 border-round text-lg font-semibold flex align-items-center justify-content-between" style={{ backgroundColor: 'rgb(39, 58, 129)' }}>
                    {th('technicalSupport.sections.chatHistory')}
                    <span className="flex align-items-center gap-3">
                        {ticketId && (
                            <Button
                                type="button"
                                label={th('technicalSupport.downloadAll')}
                                icon="pi pi-download"
                                onClick={handleDownloadAllCommentDocuments}
                                loading={isDownloadingCommentDocuments}
                                disabled={isDownloadingCommentDocuments || areCommentDocumentsLoading || commentDocumentIds.length === 0}
                                className="p-button-sm p-button-outlined text-white border-white"
                            />
                        )}
                        {ticketId && (
                            <span className="flex align-items-center gap-2 text-sm font-normal">
                                <i className="pi pi-circle-fill" style={{ fontSize: '0.6rem', color: isConnected ? '#4caf50' : '#f44336' }} />
                                {isConnected ? 'Live' : 'Offline'}
                            </span>
                        )}
                    </span>
                </h3>
            </div>

            {/* Chat History Content */}
            <div className="col-12">
                <BlockUI
                    blocked={isFileUploadInProgress}
                    template={
                        <div className="flex flex-column align-items-center gap-2">
                            <ProgressSpinner style={{ width: '3rem', height: '3rem' }} />
                            <span className="font-semibold text-900">{th('technicalSupport.messages.uploadDocumentInProgress')}</span>
                        </div>
                    }
                >
                    <div className="field">
                        {/* Chat Messages */}
                        <div
                            ref={chatContainerRef}
                            onScroll={handleChatScroll}
                            className="border-1 border-300 border-round p-4 mb-3"
                            style={{
                                maxHeight: '450px',
                                overflowY: 'auto',
                                backgroundColor: '#f8f9fa',
                                backgroundImage: 'linear-gradient(to bottom, #ffffff 0%, #f8f9fa 100%)'
                            }}
                        >
                            {allComments.length === 0 ? (
                                <div className="text-center py-5">
                                    <i className="pi pi-comments text-400" style={{ fontSize: '3rem' }} />
                                    <p className="text-500 mt-3 mb-0">{th('technicalSupport.noChatHistory')}</p>
                                </div>
                            ) : (
                                <div className="flex flex-column gap-3">
                                    {allComments.map((message, index) => {
                                        const isCurrentUser = Number(message?.createdBy) > 0 && Number(currentUserId) > 0 && Number(message.createdBy) === Number(currentUserId);
                                        const userColor = getUserColor(message.userName);
                                        const bgColor = isCurrentUser ? '#e3f2fd' : 'white';
                                        const commentId = getCommentId(message);

                                        const userMessages = allComments.filter((msg) => Number(msg?.createdBy) === Number(currentUserId));
                                        const isLastUserMessage = isCurrentUser && userMessages.length > 0 && getCommentId(userMessages[userMessages.length - 1]) === commentId;

                                        return (
                                            <div key={index} className="w-full">
                                                <div
                                                    className="p-3 border-round shadow-1 w-full"
                                                    style={{
                                                        backgroundColor: bgColor,
                                                        borderLeft: `4px solid ${userColor}`
                                                    }}
                                                >
                                                    <div className="flex align-items-center gap-2 mb-2">
                                                        <i className={`pi ${isCurrentUser ? 'pi-user' : 'pi-user-plus'}`} style={{ color: userColor }} />
                                                        <span className="font-semibold text-900">{message.userName}</span>
                                                    </div>
                                                    {/* Yeni mesajlar sunucuda temizlenmiş HTML; eski kayıtlar düz metin. */}
                                                    <RichTextEditor value={message.message} display className="mb-2 text-700 line-height-3" />
                                                    <div className="text-right">
                                                        <span className="text-xs text-500">
                                                            {new Date(message.timestamp).toLocaleString('tr-TR', {
                                                                day: '2-digit',
                                                                month: '2-digit',
                                                                year: 'numeric',
                                                                hour: '2-digit',
                                                                minute: '2-digit'
                                                            })}
                                                        </span>
                                                    </div>

                                                    {/* Comment Documents */}
                                                    {commentId && (
                                                        <CommentDocuments
                                                            commentId={commentId}
                                                            isCommentOwner={isCurrentUser}
                                                            isLastUserMessage={isLastUserMessage}
                                                            onDocumentsChange={handleCommentDocumentsChange}
                                                            onDownloadDocument={handleDownloadDocument}
                                                            onDeleteDocument={handleDeleteDocument}
                                                            onUploadDocument={handleUploadDocument}
                                                            isUploading={uploadingCommentId === commentId}
                                                            disabled={disabled}
                                                            th={th}
                                                        />
                                                    )}
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                        </div>

                        {/* New Message Input */}
                        <div className="surface-card border-1 border-300 border-round p-3">
                            <div className="grid">
                                <div className="col-12">
                                    <RichTextEditor
                                        id="chatMessageEditor"
                                        value={newMessage}
                                        onChange={onMessageChange}
                                        maxLength={CHAT_MESSAGE_MAX_LENGTH}
                                        onValidityChange={handleEditorValidityChange}
                                        placeholder={th('technicalSupport.typeMessage')}
                                        readOnly={disabled}
                                    />
                                    {pendingFiles.length > 0 && (
                                        <div className="flex flex-column gap-1 mt-2">
                                            {pendingFiles.map((file, index) => (
                                                <div key={`${file.name}-${index}`} className="flex align-items-center gap-2 text-sm" style={{ maxWidth: '40rem' }}>
                                                    <i className="pi pi-paperclip text-500" />
                                                    <span className="flex-1 text-700 text-overflow-ellipsis overflow-hidden white-space-nowrap">{file.name}</span>
                                                    <Button type="button" icon="pi pi-times" className="p-button-rounded p-button-text p-button-danger p-button-sm" onClick={() => removePendingFile(file)} disabled={isSending || isUploading} />
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                    {!disabled && (
                                        <div className="flex justify-content-end mt-2">
                                            <FileUpload
                                                ref={messageFileUploadRef}
                                                mode="basic"
                                                name="file"
                                                accept="image/*,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,text/plain,application/octet-stream,application/zip,application/vnd.rar,application/x-rar-compressed,.pdf,.doc,.docx,.xls,.xlsx,.txt,.trc,.zip,.rar"
                                                maxFileSize={104857600}
                                                chooseLabel={th('technicalSupport.attachFile')}
                                                className="p-button-sm w-auto"
                                                multiple
                                                customUpload
                                                auto={false}
                                                onSelect={handleMessageFileSelect}
                                                disabled={isSending || isUploading}
                                            />
                                        </div>
                                    )}
                                </div>
                                <div className="col-12">
                                    <Button
                                       
                                        icon="pi pi-send"
                                       
                                        label={th('technicalSupport.send')}
                                       
                                        onClick={handleSendMessage}
                                       
                                        disabled={!canSendMessage || isSending || isUploading || disabled}
                                       
                                        loading={isSending || isUploading}
                                       
                                        className="w-full"
                                   
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                </BlockUI>
            </div>

            <Toast ref={toastRef} />
            <ConfirmDialog style={{ width: '30rem' }} breakpoints={{ '576px': '90vw' }} contentStyle={{ overflowWrap: 'anywhere' }} />
        </>
    );
};

export default ChatHistory;
